// A control only has the layers its own real-world design uses — e.g. a
// rain barrel has no soil layer, a vegetative swale has no drain. This app
// has no LID hydrology yet — these numbers are carried through unevaluated.

import { nextAvailableName } from "./nextAvailableName.ts";
import { parseNum } from "./parseNum.ts";
import { writeOrDeleteSection } from "./writeOrDeleteSection.ts";

export const LID_TYPES = ["BC", "RG", "GR", "IT", "PP", "RB", "VS", "RD"] as const;
export type LidType = (typeof LID_TYPES)[number];

export interface LidSurface {
  storageHeight: number;
  vegVolFrac: number;
  roughness: number;
  surfSlope: number;
  sideSlope: number;
}

export interface LidSoil {
  thickness: number;
  porosity: number;
  fieldCap: number;
  wiltPoint: number;
  kSat: number;
  kSlope: number;
  suction: number;
}

export interface LidStorage {
  thickness: number;
  voidRatio: number;
  kSat: number;
  clogFactor: number;
  covered: boolean;
}

export interface LidPavement {
  thickness: number;
  voidRatio: number;
  impervFrac: number;
  permeability: number;
  clogFactor: number;
  regenDays: number;
  regenDegree: number;
}

export interface LidDrain {
  coeff: number;
  expon: number;
  offset: number;
  delay: number;
  hOpen: number;
  hClose: number;
  /** "" when none — the .inp file itself uses "*" for this. */
  curve: string;
}

export interface LidDrainMat {
  thickness: number;
  voidRatio: number;
  roughness: number;
}

export interface LidRemoval {
  pollutant: string;
  percent: number;
}

// `type` doesn't statically narrow which layers below are non-null for a
// given type — deliberately not a discriminated union: real per-type
// validity isn't a plain "valid or not" table (a required layer can also
// need a specific field above a threshold, e.g. PP's PAVEMENT row needs
// `thickness > 0`; GREEN_ROOF's "storage" concept is really its DRAINMAT
// row; RAIN_BARREL/INFIL_TRENCH require STORAGE while BIO_CELL/RAIN_GARDEN/
// POROUS_PAVEMENT can validly go without one). Encoding that nuance in the
// type system would either be wrong or force a large, risky rewrite of every
// consumer (13 files) for a hydrology engine already covered by fixture
// tests. `readLidUnitConfigs` (engine/lid/lid.ts) is the single authoritative
// place that enforces the real per-type layer requirements at read time,
// with a warning for every violation — treat its checks, not this comment,
// as the source of truth for which layers a given `type` actually needs.
export interface LidControl {
  name: string;
  type: LidType;
  surface: LidSurface | null;
  soil: LidSoil | null;
  storage: LidStorage | null;
  pavement: LidPavement | null;
  drain: LidDrain | null;
  drainMat: LidDrainMat | null;
  removals: LidRemoval[];
}

function num(
  row: string[],
  index: number,
  field: string,
  name: string,
  keyword: string,
  warnings?: Set<string>
): number {
  return parseNum(row[index], 0, `[LID_CONTROLS] "${name}" ${keyword} ${field}`, warnings);
}

export function readLidControls(
  raw: ReadonlyMap<string, string[][]>,
  warnings?: Set<string>
): LidControl[] {
  const rows = raw.get("LID_CONTROLS") ?? [];
  const order: string[] = [];
  const rowsByName = new Map<string, string[][]>();
  for (const row of rows) {
    const name = row[0];
    if (!name) continue;
    if (!rowsByName.has(name)) {
      rowsByName.set(name, []);
      order.push(name);
    }
    rowsByName.get(name)!.push(row);
  }

  return order.map((name) => {
    const control: LidControl = {
      name,
      type: "BC",
      surface: null,
      soil: null,
      storage: null,
      pavement: null,
      drain: null,
      drainMat: null,
      removals: [],
    };
    for (const row of rowsByName.get(name)!) {
      const keyword = row[1]?.toUpperCase() ?? "";
      if (row.length === 2 && (LID_TYPES as readonly string[]).includes(keyword)) {
        control.type = keyword as LidType;
        continue;
      }
      const n = (index: number, field: string) => num(row, index, field, name, keyword, warnings);
      switch (keyword) {
        case "SURFACE":
          control.surface = {
            storageHeight: n(2, "Storage Height"),
            vegVolFrac: n(3, "Vegetation Volume Fraction"),
            roughness: n(4, "Surface Roughness"),
            surfSlope: n(5, "Surface Slope"),
            sideSlope: n(6, "Swale Side Slope"),
          };
          break;
        case "SOIL":
          control.soil = {
            thickness: n(2, "Thickness"),
            porosity: n(3, "Porosity"),
            fieldCap: n(4, "Field Capacity"),
            wiltPoint: n(5, "Wilting Point"),
            kSat: n(6, "Conductivity"),
            kSlope: n(7, "Conductivity Slope"),
            suction: n(8, "Suction Head"),
          };
          break;
        case "STORAGE":
          control.storage = {
            thickness: n(2, "Thickness"),
            voidRatio: n(3, "Void Ratio"),
            kSat: n(4, "Conductivity"),
            clogFactor: n(5, "Clogging Factor"),
            covered: (row[6] ?? "").toUpperCase() === "YES",
          };
          break;
        case "PAVEMENT":
          control.pavement = {
            thickness: n(2, "Thickness"),
            voidRatio: n(3, "Void Ratio"),
            impervFrac: n(4, "Impervious Surface Fraction"),
            permeability: n(5, "Permeability"),
            clogFactor: n(6, "Clogging Factor"),
            regenDays: n(7, "Regeneration Interval"),
            regenDegree: n(8, "Regeneration Degree"),
          };
          break;
        case "DRAIN": {
          const curveToken = row[8];
          control.drain = {
            coeff: n(2, "Flow Coefficient"),
            expon: n(3, "Flow Exponent"),
            offset: n(4, "Offset Height"),
            delay: n(5, "Delay"),
            hOpen: n(6, "Open Head"),
            hClose: n(7, "Close Head"),
            curve: !curveToken || curveToken === "*" ? "" : curveToken,
          };
          break;
        }
        case "DRAINMAT":
          control.drainMat = {
            thickness: n(2, "Thickness"),
            voidRatio: n(3, "Void Ratio"),
            roughness: n(4, "Roughness"),
          };
          break;
        case "REMOVALS": {
          const removals: LidRemoval[] = [];
          for (let i = 2; i + 1 < row.length; i += 2) {
            removals.push({
              pollutant: row[i],
              percent: parseNum(
                row[i + 1],
                0,
                `[LID_CONTROLS] "${name}" REMOVALS "${row[i]}"`,
                warnings
              ),
            });
          }
          control.removals = removals;
          break;
        }
      }
    }
    return control;
  });
}

export function writeLidControls(
  raw: ReadonlyMap<string, string[][]>,
  controls: LidControl[]
): Map<string, string[][]> {
  const rows: string[][] = [];
  for (const c of controls) {
    rows.push([c.name, c.type]);
    if (c.surface) {
      const s = c.surface;
      rows.push([
        c.name,
        "SURFACE",
        String(s.storageHeight),
        String(s.vegVolFrac),
        String(s.roughness),
        String(s.surfSlope),
        String(s.sideSlope),
      ]);
    }
    if (c.soil) {
      const s = c.soil;
      rows.push([
        c.name,
        "SOIL",
        String(s.thickness),
        String(s.porosity),
        String(s.fieldCap),
        String(s.wiltPoint),
        String(s.kSat),
        String(s.kSlope),
        String(s.suction),
      ]);
    }
    if (c.storage) {
      const s = c.storage;
      rows.push([
        c.name,
        "STORAGE",
        String(s.thickness),
        String(s.voidRatio),
        String(s.kSat),
        String(s.clogFactor),
        s.covered ? "YES" : "NO",
      ]);
    }
    if (c.pavement) {
      const p = c.pavement;
      rows.push([
        c.name,
        "PAVEMENT",
        String(p.thickness),
        String(p.voidRatio),
        String(p.impervFrac),
        String(p.permeability),
        String(p.clogFactor),
        String(p.regenDays),
        String(p.regenDegree),
      ]);
    }
    if (c.drain) {
      const d = c.drain;
      rows.push([
        c.name,
        "DRAIN",
        String(d.coeff),
        String(d.expon),
        String(d.offset),
        String(d.delay),
        String(d.hOpen),
        String(d.hClose),
        d.curve || "*",
      ]);
    }
    if (c.drainMat) {
      const d = c.drainMat;
      rows.push([
        c.name,
        "DRAINMAT",
        String(d.thickness),
        String(d.voidRatio),
        String(d.roughness),
      ]);
    }
    if (c.removals.length > 0) {
      const row = [c.name, "REMOVALS"];
      for (const r of c.removals) row.push(r.pollutant, String(r.percent));
      rows.push(row);
    }
  }
  return writeOrDeleteSection(raw, "LID_CONTROLS", rows);
}

export function nextAvailableLidControlName(existingNames: Set<string>): string {
  return nextAvailableName(existingNames, "LID");
}
